import React from 'react';
import { Checkbox, Form, Header, Icon, Message, Segment } from 'semantic-ui-react';
import { EstadoVerificacion } from '.';

interface Props {
    idProceso: number;
    aprobacion: Aprobacion;
    verificacion: EstadoVerificacion;
    pdf: string;
    actualizarEstadoVerificacion(
        id: number,
        tieneFirmas: boolean,
        tieneHuellas: boolean,
        tieneFotocopias: boolean,
    ): Promise<EstadoVerificacion | void>;
    aprobarMatricula(id: number): Promise<Aprobacion | void>;
    rechazarMatricula(id: number, motivo: string): Promise<Aprobacion | void>;
}

type EstadosSupervision = 'aprobado' | 'rechazado' | 'sin-supervision';
interface Aprobacion {
    estado: EstadosSupervision;
    motivo: string;
}

interface State {
    botonRechazar: boolean;
    botonEditarVerificacion: boolean;
    aprobacion: Aprobacion;
    verificacion: EstadoVerificacion;
    url?: string;
    error?: {
        mensaje: string;
        detalles: Array<string>;
    };
}

export class PanelDeSupervision extends React.Component<Props, State> {
    public state: State = {
        botonRechazar: false,
        botonEditarVerificacion: false,
        aprobacion: this.props.aprobacion,
        verificacion: this.props.verificacion,
    };

    public componentDidMount() {
        Promise.resolve()
            .then(async () => {
                const { pdf } = this.props;
                const url = URL.createObjectURL(
                    await (await fetch(`data:application/pdf;base64,${pdf}`)).blob(),
                );
                this.setState({ url });
            })
            .catch();
    }

    public componentWillUnmount() {
        const { url } = this.state;
        if (url) {
            URL.revokeObjectURL(url);
        }
    }

    public render() {
        const {
            aprobarMatricula,
            idProceso: idMatricula,
            rechazarMatricula,
            actualizarEstadoVerificacion,
        } = this.props;
        const { url } = this.state;

        return (
            <Segment raised>
                <Header as='h2' content='Supervisión' />
                {url && (
                    <a href={url} target='_blank' rel='noreferrer noopener'>
                        <Icon name='file pdf outline' />
                        Abrir documentos en PDF
                    </a>
                )}
                {this.segmentoVerificacion(actualizarEstadoVerificacion)}
                {this.segmentoAprobacionRechazo(aprobarMatricula, idMatricula, rechazarMatricula)}
            </Segment>
        );
    }

    private segmentoAprobacionRechazo(
        aprobarMatricula: (id: number) => Promise<void | Aprobacion>,
        idMatricula: number,
        rechazarMatricula: (id: number, motivo: string) => Promise<void | Aprobacion>,
    ) {
        return (
            <Segment>
                <Message
                    positive={this.state.aprobacion.estado === 'aprobado'}
                    negative={this.state.aprobacion.estado === 'rechazado'}
                    warning={this.state.aprobacion.estado === 'sin-supervision'}
                >
                    <Message.Header>
                        {nombresEstadosDeAprobacion[this.state.aprobacion.estado]}
                    </Message.Header>

                    <Message.Content>
                        {this.state.aprobacion.estado === 'rechazado' && (
                            <p style={{ whiteSpace: 'pre-line' }}>{this.props.aprobacion.motivo}</p>
                        )}
                    </Message.Content>
                </Message>
                <Form>
                    {this.state.botonRechazar && (
                        <Form.TextArea
                            label='Motivos'
                            placeholder='Motivos para rechazar la matricula...'
                            value={this.state.aprobacion.motivo}
                            onChange={(_e, { value }) => {
                                this.setState({
                                    aprobacion: {
                                        estado: this.state.aprobacion.estado,
                                        motivo: value as string,
                                    },
                                });
                            }}
                        />
                    )}
                    <Form.Group inline>
                        {this.state.botonRechazar ? (
                            <Form.Button
                                content='Atrás'
                                onClick={() => this.setState({ botonRechazar: false })}
                            />
                        ) : (
                            <Form.Button
                                positive
                                disabled={this.state.verificacion.estado === 'pendiente'}
                                content='Aprobar'
                                onClick={() => {
                                    aprobarMatricula(idMatricula)
                                        .then(aprobacion => {
                                            if (aprobacion) {
                                                this.setState({ aprobacion });
                                            }
                                        })
                                        .catch();
                                }}
                            />
                        )}
                        {this.state.botonRechazar ? (
                            <Form.Button
                                negative
                                disabled={tieneSoloEspaciosEnBlanco(this.state.aprobacion.motivo)}
                                content='Rechazar!'
                                onClick={() => {
                                    rechazarMatricula(idMatricula, this.state.aprobacion.motivo)
                                        .then(async aprobacion => {
                                            if (aprobacion) {
                                                this.setState({
                                                    aprobacion,
                                                    botonRechazar: false,
                                                });
                                            }
                                        })
                                        .catch();
                                }}
                            />
                        ) : (
                            <Form.Button
                                content='Rechazar'
                                onClick={() =>
                                    this.setState({
                                        botonRechazar: true,
                                    })
                                }
                            />
                        )}
                    </Form.Group>
                </Form>
            </Segment>
        );
    }

    private segmentoVerificacion(
        actualizarEstadoVerificacion: (
            id: number,
            tieneFirmas: boolean,
            tieneHuellas: boolean,
            tieneFotocopias: boolean,
        ) => Promise<void | EstadoVerificacion>,
    ) {
        return (
            <Segment>
                <Form>
                    <Message
                        negative={this.state.verificacion.estado === 'pendiente'}
                        positive={this.state.verificacion.estado === 'verificada'}
                    >
                        <Form.Field>
                            <Checkbox
                                label='Fotocopias generadas'
                                name='grupoVerificacion'
                                disabled={!this.state.botonEditarVerificacion}
                                // value={this.state.verificacion.tieneFotocopias}
                                checked={this.state.verificacion.tieneFotocopias}
                                onChange={(_e, { checked }) => {
                                    this.setState({
                                        verificacion: {
                                            ...this.state.verificacion,
                                            tieneFotocopias: checked as boolean,
                                        },
                                    });
                                }}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox
                                label='Huellas digitales son iguales'
                                name='grupoVerificacion'
                                disabled={!this.state.botonEditarVerificacion}
                                checked={this.state.verificacion.tieneHuellas}
                                onChange={(_e, { checked }) => {
                                    this.setState({
                                        verificacion: {
                                            ...this.state.verificacion,
                                            tieneHuellas: checked as boolean,
                                        },
                                    });
                                }}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox
                                label='Tiene firmas'
                                name='grupoVerificacion'
                                disabled={!this.state.botonEditarVerificacion}
                                checked={this.state.verificacion.tieneFirmas}
                                onChange={(_e, { checked }) => {
                                    this.setState({
                                        verificacion: {
                                            ...this.state.verificacion,
                                            tieneFirmas: checked as boolean,
                                        },
                                    });
                                }}
                            />
                        </Form.Field>
                    </Message>
                    {this.state.botonEditarVerificacion ? (
                        <Form.Group>
                            <Form.Button
                                positive
                                content='Actualizar verificación'
                                onClick={() => {
                                    actualizarEstadoVerificacion(
                                        this.props.idProceso,
                                        this.state.verificacion.tieneFirmas,
                                        this.state.verificacion.tieneFotocopias,
                                        this.state.verificacion.tieneHuellas,
                                    )
                                        .then(verificacion => {
                                            if (verificacion) {
                                                this.setState({
                                                    verificacion,
                                                    botonEditarVerificacion: false,
                                                });
                                            }
                                        })
                                        .catch();
                                }}
                            />
                            <Form.Button
                                content='Atrás'
                                onClick={() =>
                                    this.setState({
                                        botonEditarVerificacion: false,
                                        verificacion: this.props.verificacion,
                                    })
                                }
                            />
                        </Form.Group>
                    ) : (
                        <Form.Group>
                            <Form.Button
                                content='Editar verificación'
                                onClick={() =>
                                    this.setState({
                                        botonEditarVerificacion: true,
                                    })
                                }
                            />
                        </Form.Group>
                    )}
                </Form>
            </Segment>
        );
    }
}

const espaciosRegExp = /^\s*$/;

function tieneSoloEspaciosEnBlanco(texto: string) {
    return espaciosRegExp.test(texto);
}

const nombresEstadosDeAprobacion: Record<EstadosSupervision, string> = {
    'aprobado': 'Aprobada',
    'rechazado': 'Rechazada',
    'sin-supervision': 'Sin supervisión',
};

export default PanelDeSupervision;



// WEBPACK FOOTER //
// ./src/modulos/Matriculas/Supervision/ProcesoDeMatricula/PanelDeSupervision.tsx